// The indeterminate loading spinner, drawn exactly as Blueprint drew it.
//
// Blueprint's version is a 100-unit circle with a quarter-arc head, in three
// nested elements: an svg cannot be animated reliably on its own, so the
// rotation goes on a wrapper, and a second wrapper centers it. The geometry is
// reproduced rather than reinvented because the whole point of removing
// Blueprint is that nothing on screen changes.
//
// Only what packages/ui actually used is here: one size, no intent colors, no
// determinate value, no dark variant. Blueprint offered all of those and no
// caller in either tree asked for any of them.

import { styleAsClass } from "../../styles/styleAsClass";

/** Radius of the circle in the 100-unit viewport. */
const RADIUS = 45;

/**
 * The arc, as two half-circles. The stroke-dash properties below are relative
 * to this arbitrary length because the pathLength attribute normalizes the
 * browser's measured path to 280 units.
 */
const PATH_LENGTH = 280;

const TRACK = `M 50,50 m 0,-${RADIUS} a ${RADIUS},${RADIUS} 0 1 1 0,${RADIUS * 2} a ${RADIUS},${RADIUS} 0 1 1 0,-${RADIUS * 2}`;

/** The head covers a quarter of the circle. */
const HEAD_OFFSET = PATH_LENGTH * 0.75;

/** Stroke stays 4 units wide until the spinner is small enough that it cannot. */
const STROKE_WIDTH = 4;
const REFERENCE_SIZE = 100;
const MAX_STROKE_WIDTH = 16;
const MIN_SIZE = 10;

const ROOT = styleAsClass`
  align-items: center;
  display: flex;
  justify-content: center;
  overflow: visible;
  vertical-align: middle;

  svg {
    display: block;
  }

  path {
    fill-opacity: 0;
  }
`;

/** Sits inline with text instead of taking a line of its own. */
const INLINE = styleAsClass`
  display: inline-flex;
`;

const ANIMATION = styleAsClass`
  animation: core-spinner-rotate 500ms linear infinite;

  @keyframes core-spinner-rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const TRACK_STROKE = styleAsClass`
  stroke: rgba(95, 107, 124, 0.2);
`;

const HEAD_STROKE = styleAsClass`
  stroke: rgba(95, 107, 124, 0.8);
  stroke-linecap: round;
  transform-origin: center;
`;

/** The frame is grown by half the stroke so the arc sits on its edge. */
function viewBox(strokeWidth: number): string {
  const radius = RADIUS + strokeWidth / 2;
  const origin = (50 - radius).toFixed(2);
  const extent = (radius * 2).toFixed(2);
  return `${origin} ${origin} ${extent} ${extent}`;
}

export interface SpinnerProps {
  /** Outer diameter in pixels. Anything under 10 is drawn at 10. */
  size?: number;
  /** Sits inline with surrounding text. */
  inline?: boolean;
  className?: string;
  "data-test"?: string;
}

export function Spinner({
  size = 50,
  inline = false,
  className,
  ...rest
}: SpinnerProps) {
  const sizePx = Math.max(MIN_SIZE, size);
  const strokeWidth = Math.min(
    MAX_STROKE_WIDTH,
    (STROKE_WIDTH * REFERENCE_SIZE) / sizePx,
  );
  return (
    <div
      aria-label="loading"
      aria-valuemax={100}
      aria-valuemin={0}
      className={[ROOT, inline ? INLINE : "", className]
        .filter(Boolean)
        .join(" ")}
      role="progressbar"
      {...rest}
    >
      <div className={ANIMATION}>
        <svg
          width={sizePx}
          height={sizePx}
          strokeWidth={strokeWidth.toFixed(2)}
          viewBox={viewBox(strokeWidth)}
        >
          <path className={TRACK_STROKE} d={TRACK} />
          <path
            className={HEAD_STROKE}
            d={TRACK}
            pathLength={PATH_LENGTH}
            strokeDasharray={`${PATH_LENGTH} ${PATH_LENGTH}`}
            strokeDashoffset={HEAD_OFFSET}
          />
        </svg>
      </div>
    </div>
  );
}
